import { useCallback, useEffect, useMemo } from "react";

import { useFilterContext } from "../context/FilterContext";

export interface UseSingleSelectFilterOptions {
  queryKey: string;
  validValues: string[];
  getOptionLabel?: (value: string) => string | undefined;
}

export interface UseSingleSelectFilterResult {
  selectedValue: string | null;
  setSelectedValue: (value: string | null) => void;
  chipLabel: string | undefined;
}

export const useSingleSelectFilter = ({
  queryKey,
  validValues,
  getOptionLabel,
}: UseSingleSelectFilterOptions): UseSingleSelectFilterResult => {
  const { filterState, setFilter, removeFilter } = useFilterContext();
  const raw = filterState[queryKey] ?? null;

  const validSet = useMemo(() => new Set(validValues), [validValues]);

  const selectedValue = useMemo(() => {
    if (raw === null) return null;
    return validSet.has(raw) ? raw : null;
  }, [raw, validSet]);

  useEffect(() => {
    if (raw !== null && !validSet.has(raw)) {
      removeFilter(queryKey);
    }
  }, [raw, validSet, queryKey, removeFilter]);

  const setSelectedValue = useCallback(
    (value: string | null) => {
      if (value) {
        setFilter(queryKey, value);
      } else {
        removeFilter(queryKey);
      }
    },
    [queryKey, setFilter, removeFilter]
  );

  const chipLabel = useMemo(() => {
    if (!selectedValue || !getOptionLabel) return undefined;
    return getOptionLabel(selectedValue);
  }, [selectedValue, getOptionLabel]);

  return { selectedValue, setSelectedValue, chipLabel };
};
